import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getJenkinsJobs } from '../modules/automation';
import { getConfigurations, getTestCycleData } from '../modules/config';
import { getAssociatedUsers, getUserProjectList } from '../modules/project';
import { listAllTestCycles } from '../modules/testcycles';
import { listAllUsers } from '../modules/users';
import QUERY_KEYS from './queryKeys';

export const useUsersList = (params = {}) =>
  useQuery({
    queryKey: QUERY_KEYS.users.list(params),
    queryFn: () => listAllUsers(
      params.perPage,
      params.page,
      params.search,
      params.status,
      params.includeProject,
      params.projectId
    ),
    keepPreviousData: true,
  });

export const useUserProjects = (params = {}) =>
  useQuery({
    queryKey: QUERY_KEYS.project.list(params),
    queryFn: () => getUserProjectList(
      params.perPage,
      params.page,
      params.search,
      params.includeTestCases,
      params.includeLinkedProjects
    ),
  });

export const useInvalidateQuery = () => {
  const queryClient = useQueryClient();

  return (keys = []) => {
    keys.forEach(({ key, type = 'list', params = {} }) => {
      const queryKey = QUERY_KEYS?.[key]?.[type]
        ? QUERY_KEYS[key][type](params)
        : [key];

      queryClient.invalidateQueries({ queryKey });
    });
  };
};

export const useTestCycles = ({
  page,
  perPage,
  search,
  queryString,
  projectId,
  sortBy,
  sortOrder,
}) =>
  useQuery({
    queryKey: QUERY_KEYS.testCycle.listWithParams({
      page,
      perPage,
      search,
      queryString,
      projectId,
      sortBy,
      sortOrder,
    }),
    queryFn: () =>
      listAllTestCycles(page, perPage, search, queryString, projectId, sortBy, sortOrder),
    keepPreviousData: true,
  });

export const useEntityConfig = (entityType,projectId) =>
  useQuery({
    queryKey: ['entity-config', projectId, entityType],
    queryFn: async () => {
      const response = await getConfigurations(entityType, projectId);
      return response?.data || [];
    },
    enabled: !!projectId,
  });

export const useTestCycleConfig = (projectId) =>
  useQuery({
    queryKey: ['testcycle-config', projectId],
    queryFn: async () => {
      const response = await getTestCycleData(projectId);
      return response?.data || [];
    },
    enabled: !!projectId,
  });

export const useProjectUsers = (projectId) =>
  useQuery({
    queryKey: ['project-users', projectId],
    queryFn: async () => {
      const response = await getAssociatedUsers(projectId);
      return response.map((association) => ({
        ...association.user,
        role: association.role,
      }));
    },
    enabled: !!projectId,
  });

export const useJenkinsJobs = (projectId) =>
  useQuery({
    queryKey: ['jenkins-jobs', projectId],
    queryFn: async () => {
      const response = await getJenkinsJobs(projectId);
      return response?.data || [];
    },
    enabled: !!projectId,
  });
